import { FunctionComponent } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './SingleChoice.module.css';

interface Option {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface SingleChoiceProps {
  id: string;
  label: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  variant?: 'inline' | 'vertical' | 'dropdown';
  required?: boolean;
  helperText?: string;
  error?: string;
  hasNextButton?: boolean;
  onNext?: () => void;
}

const SingleChoice: FunctionComponent<SingleChoiceProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  variant = 'vertical',
  required = false,
  helperText,
  error,
  hasNextButton = false,
  onNext
}) => {
  if (variant === 'dropdown') {
    return (
      <div className={styles.container}>
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
        <select
          id={id}
          className={`${styles.dropdown} ${error ? styles.error : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        >
          <option value="">Select an option</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {helperText && <p className={styles.helperText}>{helperText}</p>}
        {error && <p className={styles.errorText}>{error}</p>}
        
        {hasNextButton && (
          <button 
            type="button" 
            className={styles.nextButton} 
            onClick={onNext}
            disabled={required && !value}
          >
            Next
            <ChevronRightIcon />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <fieldset className={styles.fieldset}>
        <legend className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </legend>
        <div className={`${styles.optionsContainer} ${variant === 'inline' ? styles.inline : styles.vertical}`}>
          {options.map((option) => (
            <label key={option.value} className={styles.option}>
              <input
                type="radio"
                name={id}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange(e.target.value)}
                className={styles.radio}
                required={required}
              />
              <div className={styles.optionContent}>
                {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                <span className={styles.optionLabel}>{option.label}</span>
              </div>
            </label>
          ))}
        </div>
      </fieldset>
      {helperText && <p className={styles.helperText}>{helperText}</p>}
      {error && <p className={styles.errorText}>{error}</p>}
      
      {hasNextButton && (
        <button 
          type="button" 
          className={styles.nextButton} 
          onClick={onNext}
          disabled={required && !value}
        >
          Next
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

export default SingleChoice;